import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime, formatNumber } from "@/lib/format";
import { formatUsd } from "@/components/LlmExtractionFields";

type ReceiptLlmExtraction = components["schemas"]["ReceiptLlmExtractionResponse"];

interface ReceiptLlmExtractionsTableProps {
  organisationId?: string;
  refreshKey?: number;
}

// Deliberately separate from LlmExtractionsTable: different routes and shapes, easier compared side by side.
export function ReceiptLlmExtractionsTable({
  organisationId,
  refreshKey,
}: ReceiptLlmExtractionsTableProps) {
  const pagination = useCursorPagination({ initialLimit: 50 });
  const { reset } = pagination;

  const [rows, setRows] = useState<ReceiptLlmExtraction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reset();
  }, [organisationId, refreshKey, reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchRows() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/receipt-llm-extractions", {
        params: {
          query: {
            Cursor: pagination.cursor,
            Limit: pagination.limit,
            OrganisationId: organisationId,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load receipt extractions.");
      } else {
        setRows(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchRows();
    return () => {
      cancelled = true;
    };
  }, [organisationId, refreshKey, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead>Organisation</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Input</TableHead>
              <TableHead className="text-right">Output</TableHead>
              <TableHead className="text-right">Total cost</TableHead>
              <TableHead className="text-right">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground h-24 text-center">
                  No receipt extractions.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.receiptId}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {formatDateTime(row.extractedAt)}
                  </TableCell>
                  <TableCell>
                    <IdBadge id={row.receiptId} />
                  </TableCell>
                  <TableCell>
                    <IdBadge id={row.organisationId} to={`/organisations/${row.organisationId}`} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {row.model}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.inputTokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.outputTokens)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatUsd(row.totalCostUsd)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(Math.round(Number(row.durationMs ?? 0)))} ms
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        limitOptions={[20, 50, 100]}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}

// Receipts have no admin page yet, so the id is a copyable badge rather than a dead link.
function IdBadge({ id, to }: { id?: string; to?: string }) {
  if (!id) return <span className="text-muted-foreground">-</span>;

  const badge = (
    <Badge
      variant="secondary"
      className={`font-mono text-xs${to ? " hover:bg-primary/20" : ""}`}
      title={id}
    >
      {id.slice(0, 8)}...
    </Badge>
  );

  if (!to) return badge;

  return <Link to={to}>{badge}</Link>;
}
